import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | valid | error | success
  const [invitation, setInvitation] = useState(null);
  const [tokenError, setTokenError] = useState('');

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('No invitation token found. Please use the link from your invitation email.');
      setStatus('error');
      return;
    }

    axios
      .get(`${API_BASE_URL}/auth/verify-invite`, { params: { token } })
      .then((res) => {
        if (res.data.success) {
          setInvitation(res.data.data);
          setFullName(res.data.data.inviteeName || '');
          setStatus('valid');
        }
      })
      .catch((err) => {
        setTokenError(err.response?.data?.error || 'Invalid or expired invitation link.');
        setStatus('error');
      });
  }, [token]);

  const validatePassword = (pw) => {
    if (pw.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(pw)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const pwError = validatePassword(password);
    if (pwError) {
      setFormError(pwError);
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/accept-invite`, { token, password, full_name: fullName.trim() });
      if (res.data.success) {
        setStatus('success');
      }
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-grid container">
        <div className="login-hero d-none d-md-flex">
          <div className="login-hero-content">
            <div className="brand">
              <img
                src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
                alt="Zilch Logo"
                width={48}
                height={48}
              />
              <span>Zilch.</span>
            </div>
            <h2>Measure and manage your product carbon footprint.</h2>
            <p>Join your team on Zilch to run product carbon footprint assessments and share results.</p>
          </div>
          <div className="login-orb orb-1" />
          <div className="login-orb orb-2" />
          <div className="login-orb orb-3" />
        </div>

        <div className="login-card-wrapper">
          <div className="login-card">
            <div className="brand brand-center d-md-none mb-3">
              <img
                src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
                alt="Zilch Logo"
                width={40}
                height={40}
              />
              <span>Zilch.</span>
            </div>

            {status === 'loading' && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            )}

            {status === 'error' && (
              <>
                <h1 className="login-title">Invitation Invalid</h1>
                <p className="login-subtitle">{tokenError}</p>
                <div className="text-center mt-3">
                  <Link to="/login" className="zilch-text-green">
                    Sign in instead
                  </Link>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <h1 className="login-title">Account created!</h1>
                <p className="login-subtitle">
                  Your account has been set up. Sign in to start working with your team.
                </p>
                <Link to="/login" className="login-button d-block text-center text-decoration-none mt-3">
                  Sign in
                </Link>
              </>
            )}

            {status === 'valid' && invitation && (
              <>
                <h1 className="login-title">Set up your account</h1>
                <p className="login-subtitle">
                  You've been invited to join <strong>{invitation.organisationName}</strong>. Choose a password to get started.
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      className="login-input"
                      placeholder="e.g., Jane Smith"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      className="login-input"
                      value={invitation.inviteeEmail}
                      readOnly
                      style={{ backgroundColor: '#f9fafb', color: '#6b7280' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      className="login-input"
                      placeholder="Create a strong password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <small className="login-subtitle d-block mt-1">
                      Must be 8+ characters with uppercase, lowercase, and number
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      className="login-input"
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="login-button"
                    disabled={submitting || !fullName.trim() || !password || !confirmPassword}
                  >
                    {submitting ? 'Creating account…' : 'Create Account'}
                  </button>
                </form>

                {formError && (
                  <div className="login-error" role="alert">
                    {formError}
                  </div>
                )}

                <div className="text-center mt-3">
                  <small className="login-subtitle">
                    Already have an account?{' '}
                    <Link to="/login" className="zilch-text-green">
                      Sign in
                    </Link>
                  </small>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvite;
